import { GlobalStateController } from "bai-react-global-state";
import type { User } from "firebase/auth";
import _ from "lodash";
import moment from "moment";
import React from "react";

import { debug as debugApi, workspaceIntegrations as workspaceIntegrationsApi, workspaceResourceFolders as workspaceResourceFoldersApi, workspaceResources as workspaceResourcesApi, workspaces as workspacesApi, workspacesBilling as workspacesBillingApi, workspacesMetadata as workspacesMetadataApi, workspaceUserGroups as workspaceUserGroupsApi, workspaceUserInvites as workspaceUserInvitesApi, workspaceUsers as workspaceUsersApi } from "apis-v2/callables";
import type { UpdateWorkspaceRequest } from "apis-v2/workspaces/handlers";
import { presentations as presentationsApi } from "apis-v2/callables";
import { ConflictError } from "apis-v2/errors";
import {
    AnalyticsRolesAndLicensesMap,
    BEAUTIFUL_WORKSPACE_ACTION,
    PusherEventType,
    TaskState,
    TEAM_USER_LICENSE_STATUS
} from "common/constants";
import {
    ActionPermissionsObject,
    GET_DEFAULT_BASIC_PLAN,
    GET_DEFAULT_RESTRICTED_ACTION_PERMISSIONS,
    GET_DEFAULT_RESTRICTED_PLAN_PERMISSIONS,
    IPresentation,
    IUserStorageModel,
    IWorkspace,
    IWorkspaceDocumentChangedEvent,
    IWorkspaceIntegrationConfig,
    IWorkspaceMetadata,
    IWorkspacePermission,
    IWorkspacePlan,
    IWorkspaceResource,
    IWorkspaceResourceAsset,
    IWorkspaceResourceFolder,
    IWorkspaceResourcePresentation,
    IWorkspaceResourceWithDocument,
    IWorkspaceUser,
    IWorkspaceUserExtended,
    IWorkspaceUserGroup,
    IWorkspaceUserInvite,
    LegacyTeamMemberRole,
    PaginatedResponse,
    PermissionActionType,
    WorkspaceIntegrationType,
    WorkspacePermissionObjectType,
    WorkspacePermissionSubjectType,
    WorkspacePlanTier,
    WorkspacePlanType,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserGroupType,
    WorkspaceUserRole
} from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import { withMaxConcurrency } from "common/utils/withMaxConcurrency";
import {
    getActionPermissionsCombinedWithPlanPermissions,
    getCombinedActionPermissions
} from "common/utils/workspaces/permissions";
import { getLegacyRoleFromWorkspaceUserRole } from "common/utils/workspaces/users";
import Analytics from "js/analytics";
import BillingController from "js/controllers/BillingController";
import { coercePersonalFoldersTypeToShared, hasFolderTypeToCoerceToShared } from "js/controllers/coercePersonalFolderTypeToShared";
import AppController from "js/core/AppController";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import { getExperiments } from "js/core/services/experiments";
import { requestExportCache } from "js/core/services/exportCache";
import pusher, { ExtendedChannel } from "js/core/services/pusher";
import { ClipboardType, clipboardWrite } from "js/core/utilities/clipboard";
import { download, downloadFromUrl, filenameForExport, trackActivity, trackState } from "js/core/utilities/utilities";
import db from "js/db";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import { ShowDialog, ShowMessageDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import MandatoryUpdateDialog from "js/react/components/Dialogs/MandatoryUpdateDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import Spinner from "js/react/components/Spinner";
import { reactMount, reactUnmount } from "js/react/renderReactRoot";
import ConfirmSubscriptionChargeDialog from "js/react/views/Billing/dialogs/ConfirmSubscriptionChargeDialog";
import EnterpriseWorkspaceBlockedDialog from "js/react/views/Billing/dialogs/EnterpriseWorkspaceBlockedDialog";
import PaymentIssueDialog from "js/react/views/Billing/dialogs/PaymentIssueDialog";
import PlanPreviewEndedDialog from "js/react/views/Billing/dialogs/PlanPreviewEndedDialog";
import SubscriptionCanceledDialog from "js/react/views/Billing/dialogs/SubscriptionCanceledDialog";
import TrialCanceledDialog from "js/react/views/Billing/dialogs/TrialCanceledDialog";
import TrialFailedDialog from "js/react/views/Billing/dialogs/TrialFailedDialog";
import TrialPausedDialog from "js/react/views/Billing/dialogs/TrialPausedDialog";
import TrialPausedDialogForMember from "js/react/views/Billing/dialogs/TrialPausedDialogForMember";

const logger = getLogger(LogGroup.WORKSPACES);

export interface WorkspaceControllerState {
    initialized: boolean;
    initializeError: Error | null;
    workspace: IWorkspace;
    users: IWorkspaceUserExtended[];
    userInvites: IWorkspaceUserInvite[];
    userGroups: IWorkspaceUserGroup[];
    resourceFolders: IWorkspaceResourceFolder[];
    isPastDue: boolean;
    plan: IWorkspacePlan;
    experiments: {
        [experimentId: string]: boolean;
    }
}

const initialState: WorkspaceControllerState = {
    initialized: false,
    initializeError: null,
    workspace: null,
    users: [],
    userInvites: [],
    userGroups: [],
    resourceFolders: [],
    isPastDue: false,
    plan: GET_DEFAULT_BASIC_PLAN(),
    experiments: {}
};

export type OnWorkspaceResourcesChangedCallback = (change: {
    added: IWorkspaceResource[],
    removed: { resourceId: string, resourceType: WorkspaceResourceType }[],
    updated: IWorkspaceResource[]
}) => void;

class WorkspaceController extends GlobalStateController<WorkspaceControllerState> {
    private _pusherChannel: ExtendedChannel;
    private _firebaseUser: User;
    private _userModel: IUserStorageModel;
    private _runSequentially: SequentialRunner;
    private _stateDidUpdateCallbacks: ((prevState: WorkspaceControllerState, currentState: WorkspaceControllerState) => void)[] = [];
    private _onWorkspaceResourcesChangedCallbacks: OnWorkspaceResourcesChangedCallback[] = [];
    private _checkPastDueTimeout: ReturnType<typeof setTimeout>;

    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    public get workspaceId() {
        return this._state.workspace?.id;
    }

    public get workspaceUser() {
        return this._state.users.find(user => user.uid === this._firebaseUser.uid);
    }

    public get workspaceUsers() {
        return this._state.users;
    }

    public get workspaceUserGroups() {
        const { userGroups } = this._state;
        const groups = userGroups.filter(group => this.workspaceUser?.groupIds.includes(group.id));
        const getMissingGroupIds = () => {
            const groupIds = new Set<string>();
            groups.forEach(g => {
                g.groupIds.forEach(groupId => {
                    groupIds.add(groupId);
                });
            });
            return Array.from(groupIds)
                .filter(groupId => !groups.some(g => g.id === groupId))
                .filter(groupId => userGroups.some(g => g.id === groupId));
        };
        while (getMissingGroupIds().length > 0) {
            getMissingGroupIds().forEach(groupId => {
                groups.push(userGroups.find(g => g.id === groupId));
            });
        }
        return groups;
    }

    public get role() {
        if (!this.initialized) {
            return WorkspaceUserRole.GUEST;
        }

        return this.workspaceUser.role;
    }

    public get usedSeatCount() {
        return this._state.users.filter(user => user.role !== WorkspaceUserRole.GUEST).length + this._state.userInvites.filter(invite => invite.role !== WorkspaceUserRole.GUEST).length;
    }

    public get legacyRole() {
        if (!this.initialized) {
            return LegacyTeamMemberRole.MEMBER;
        }

        return getLegacyRoleFromWorkspaceUserRole(this.workspaceUser.role);
    }

    public get isOwner() {
        if (!this.initialized) {
            return false;
        }

        return this.workspaceUser.role === WorkspaceUserRole.OWNER;
    }

    public get isGuest() {
        if (!this.initialized) {
            return true;
        }
        return this.role === WorkspaceUserRole.GUEST;
    }

    public get isBasic() {
        if (!this.initialized) {
            return false;
        }
        return this.plan.tier === WorkspacePlanTier.BASIC;
    }

    public get isBasicOrGuest() {
        if (!this.initialized) {
            return true;
        }
        return this.isGuest || this.isBasic;
    }

    public get actionPermissions() {
        if (!this.initialized) {
            return GET_DEFAULT_RESTRICTED_ACTION_PERMISSIONS();
        }

        return getActionPermissionsCombinedWithPlanPermissions(
            getCombinedActionPermissions((this.workspaceUser?.groupIds.map(id => this._state.userGroups.find(group => group.id === id)?.actionPermissions) ?? []).filter(Boolean)),
            this.planPermissions
        );
    }

    public get planPermissions() {
        if (!this.initialized) {
            return GET_DEFAULT_RESTRICTED_PLAN_PERMISSIONS();
        }

        return this._state.plan.permissions;
    }

    public get isPastDue() {
        return this._state.isPastDue;
    }

    public get isEnterprise() {
        return this._state.plan.tier === WorkspacePlanTier.ENTERPRISE;
    }

    public get initialized() {
        return this._state.initialized;
    }

    public get plan() {
        return this._state.plan;
    }

    public get resourceFolders() {
        return this._state.resourceFolders;
    }

    public get userInvites() {
        return this._state.userInvites;
    }

    public get userGroups() {
        return this._state.userGroups;
    }

    public get users() {
        return this._state.users;
    }

    public get workspace() {
        return this._state.workspace;
    }

    public get experiments() {
        return this._state.experiments;
    }

    /**
     * Helper to check if the workspace is a multi-user workspace (i.e. team or enterprise)
     */
    public get isMultiUserWorkspace() {
        return [WorkspacePlanTier.TEAM, WorkspacePlanTier.ENTERPRISE].includes(this.plan.tier);
    }

    private _initialize(workspaceId: string, firebaseUser: User, userModel: IUserStorageModel) {
        return this._runSequentially(async () => {
            await this.reset(false);

            logger.info("[WorkspaceController] initialize()", { workspaceId, uid: firebaseUser.uid });

            try {
                const [workspace, users, userGroups, resourceFolders, plan, userInvites] = await Promise.all([
                    workspacesApi.getWorkspace({ workspaceId }),
                    workspaceUsersApi.getWorkspaceUsers({ workspaceId }),
                    workspaceUserGroupsApi.getWorkspaceUserGroups({ workspaceId }),
                    workspaceResourceFoldersApi.getWorkspaceResourceFolders({ workspaceId }),
                    workspacesBillingApi.getWorkspacePlan({ workspaceId }),
                    workspaceUserInvitesApi.getWorkspaceUserInvites({ workspaceId })
                ]);

                this._pusherChannel = await pusher.subscribe(`private-workspace-${workspaceId}`);
                this._pusherChannel.bind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);

                this._firebaseUser = firebaseUser;
                this._userModel = userModel;

                const { ai_gen_images } = await getExperiments(["ai_gen_images"]);

                await this._updateState({
                    workspace,
                    initialized: true,
                    initializeError: null,
                    users,
                    userGroups,
                    resourceFolders,
                    plan,
                    userInvites,
                    experiments: {
                        ai_gen_images: ai_gen_images.enabled
                    }
                });

                this._postInitialize();
            } catch (err) {
                logger.error(err, "[WorkspaceController] initialize() failed to initialize");

                await this._updateState({
                    initialized: false,
                    initializeError: err
                });
            }
        });
    }

    private async _postInitialize() {
        // Show dialog if the user's role has changed while offline
        // TODO: this should be handled via a notification
        if (this._userModel.has("workspaceRoleChange")) {
            const roleChangedForWorkspaceId = this._userModel.get("workspaceRoleChange");
            if (roleChangedForWorkspaceId === this.workspaceId) {
                this._onWorkspaceRoleChanged();
            }
        }

        await Promise.all([
            _getAnalyticsProps(AppController.workspaceMetadata)
                .then(props => Analytics.util.trackState(props))
                .catch(err => logger.error(err, "[WorkspaceController] _postInitialize() failed to track state")),
            Analytics.util.identify(this._firebaseUser)
                .catch(err => logger.error(err, "[WorkspaceController] _postInitialize() failed to identify"))
        ]);

        // Check past due renders dialogs that need analytics to be fully initialized
        this.checkPastDue();

        // Plan preview
        const { workspace } = this._state;
        if (workspace.metadata?.planPreview?.enabled) {
            const msLeft = workspace.metadata.planPreview.activeTill - Date.now();
            if (msLeft > 0) {
                this._checkPastDueTimeout = setTimeout(() => {
                    this.checkPastDue(true);
                }, msLeft);
            }
        }
    }

    private _dialogContainer: HTMLElement | null = null;

    private _removeDialogContainer() {
        if (this._dialogContainer) {
            reactUnmount(this._dialogContainer);
            this._dialogContainer.remove();
            this._dialogContainer = null;
        }
    }

    private _getDialogContainer() {
        this._removeDialogContainer();
        this._dialogContainer = document.createElement("div");
        this._dialogContainer.id = "workspace-billing-dialog-container";
        document.body.append(this._dialogContainer);
        return this._dialogContainer;
    }

    private _reset(runSequentially = true) {
        const reset = async () => {
            logger.info("[WorkspaceController] reset()");

            if (this._checkPastDueTimeout) {
                clearTimeout(this._checkPastDueTimeout);
                this._checkPastDueTimeout = null;
            }

            if (this._pusherChannel) {
                this._pusherChannel.unbind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);
                if (!this._pusherChannel.isInUse) {
                    pusher.unsubscribe(this._pusherChannel.name);
                }
                this._pusherChannel = null;
            }

            await this._updateState(_.cloneDeep(initialState));

            await BillingController.reset();
        };

        if (runSequentially) {
            return this._runSequentially(reset);
        }

        return reset();
    }

    /**
     * Temporary: new library UI treats personal folders as shared.
     * Remove after migrate-personal-folders-to-shared completes.
     */
    protected _stateWillUpdate(nextState: WorkspaceControllerState): WorkspaceControllerState {
        if (!hasFolderTypeToCoerceToShared(nextState.resourceFolders)) {
            return nextState;
        }

        return {
            ...nextState,
            resourceFolders: coercePersonalFoldersTypeToShared(nextState.resourceFolders)
        };
    }

    protected _stateDidUpdate(prevState: WorkspaceControllerState) {
        const { initialized, users, workspace, userGroups, plan } = this._state;

        try {
            this._stateDidUpdateCallbacks.forEach(callback => callback(prevState, this._state));
        } catch (err) {
            logger.error(err, "[WorkspaceController] _stateDidUpdate() failed to run stateDidUpdate callbacks");
        }

        if (!initialized) {
            return;
        }

        const handleUsersUpdate = () => {
            if (!prevState.initialized) {
                return;
            }

            if (_.isEqual(prevState.users, users)) {
                return;
            }

            const uid = this._firebaseUser.uid;

            const currentUserBefore = prevState.users.find(user => user.uid === uid);
            const currentUserAfter = this.workspaceUser;

            if (!currentUserBefore || !currentUserAfter) {
                return;
            }

            const roleBefore = getLegacyRoleFromWorkspaceUserRole(currentUserBefore.role);
            const roleAfter = this.legacyRole;
            if (roleBefore !== roleAfter) {
                this._onWorkspaceRoleChanged();
            }
        };

        const handleWorkspacesMetadataUpdate = () => {
            if (!prevState.initialized) {
                return;
            }

            if (
                prevState.workspace?.name !== workspace.name ||
                prevState.workspace?.photoURL !== workspace.photoURL ||
                !_.isEqual(prevState.plan, plan)
            ) {
                this._userModel.loadWorkspacesMetadata([this.workspaceId])
                    .catch(err => {
                        logger.error(err, "[WorkspaceController] handleWorkspacesMetadataUpdate() failed to reload user workspaces metadata");
                    });
            }
        };

        const handleActionPermissionsUpdate = () => {
            if (_.isEqual(prevState.users, users) && _.isEqual(prevState.userGroups, userGroups)) {
                return;
            }

            const uid = this._firebaseUser.uid;
            const currentUserBefore = prevState.users.find(user => user.uid === uid);
            const actionPermissionsBefore = getCombinedActionPermissions(
                (currentUserBefore?.groupIds.map(id => prevState.userGroups.find(group => group.id === id)?.actionPermissions) ?? []).filter(Boolean)
            );

            // If the user has billing access, we need the billing controller to be initialized, otherwise we need to reset it
            if (actionPermissionsBefore[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE] !== this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]) {
                if (!this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]) {
                    BillingController.reset();
                    return;
                }

                if (BillingController.initialized) {
                    return;
                }

                this._initializeBillingController();
            }
        };

        handleUsersUpdate();
        handleWorkspacesMetadataUpdate();
        handleActionPermissionsUpdate();
    }

    private _initializeBillingController() {
        return BillingController.initialize(this.workspaceId, this._firebaseUser, this._userModel)
            .catch(err => {
                logger.error(err, "[WorkspaceController] _initializeBillingController() failed to initialize BillingController");
            });
    }

    private _onPusherEvent = (event: IWorkspaceDocumentChangedEvent) => {
        this._runSequentially(async () => {
            if (event.documentType === "Workspace") {
                const workspace = event.newDocument as IWorkspace;
                if (workspace.modifiedAt > this._state.workspace.modifiedAt) {
                    await this._updateState({ workspace });
                }
            }

            if (event.documentType === "WorkspaceUser") {
                if (event.operationType === "create" || event.operationType === "update") {
                    const workspaceUser = event.newDocument as IWorkspaceUserExtended;

                    const userBeforeUpdate = this._state.users.find(user => user.uid === workspaceUser.uid);
                    if ((userBeforeUpdate?.modifiedAt ?? 0) < workspaceUser.modifiedAt) {
                        await this._updateState(state => ({
                            ...state,
                            users: _.cloneDeep([...state.users.filter(user => user.uid !== workspaceUser.uid), workspaceUser])
                        }));

                        if (
                            event.operationType === "update" &&
                            workspaceUser.uid === this._firebaseUser.uid &&
                            getObjectHash(userBeforeUpdate.groupIds) !== getObjectHash(workspaceUser.groupIds)
                        ) {
                            const addedGroupIds = workspaceUser.groupIds.filter(id => !userBeforeUpdate.groupIds.includes(id));
                            const removedGroupIds = userBeforeUpdate.groupIds.filter(id => !workspaceUser.groupIds.includes(id));
                            if (addedGroupIds.length > 0 || removedGroupIds.length > 0) {
                                await this.forceRefreshWorkspaceUserGroups(null, false);
                                if (
                                    removedGroupIds.length > 0 ||
                                    this._state.userGroups.some(group =>
                                        group.type === WorkspaceUserGroupType.RESOURCE_FOLDER &&
                                        !this._state.resourceFolders.some(folder => folder.userGroupId === group.id)
                                    )
                                ) {
                                    await this.forceRefreshWorkspaceResourceFolders(null, false);
                                }
                            }
                        }
                    }
                } else if (event.operationType === "delete") {
                    const workspaceUser = event.oldDocument as IWorkspaceUserExtended;

                    const userBeforeDelete = this._state.users.find(user => user.uid === workspaceUser.uid);
                    if ((userBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                        this._updateState(state => ({
                            ...state,
                            users: _.cloneDeep(state.users.filter(user => user.uid !== workspaceUser.uid))
                        }));
                    }
                }
            }

            if (event.documentType === "WorkspaceUserGroup") {
                const workspaceUserGroup = event.newDocument as IWorkspaceUserGroup;
                const groupId = event.documentId;

                if (event.operationType === "create" || event.operationType === "update") {
                    const groupBeforeUpdate = this._state.userGroups.find(group => group.id === groupId);
                    if ((groupBeforeUpdate?.modifiedAt ?? 0) < workspaceUserGroup.modifiedAt) {
                        this._updateState(state => ({
                            ...state,
                            userGroups: _.cloneDeep([...state.userGroups.filter(group => group.id !== groupId), workspaceUserGroup])
                        }));
                    }
                } else if (event.operationType === "delete") {
                    const groupBeforeDelete = this._state.userGroups.find(group => group.id === groupId);
                    if ((groupBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                        this._updateState(state => ({
                            ...state,
                            userGroups: _.cloneDeep(state.userGroups.filter(group => group.id !== groupId))
                        }));
                    }
                }
            }

            if (event.documentType === "WorkspaceResourceFolder") {
                const workspaceResourceFolder = event.newDocument as IWorkspaceResourceFolder;
                const folderId = event.documentId;

                const permissions = await workspaceResourceFoldersApi.getUserPermissionsToResourceFolder({
                    workspaceId: this.workspaceId,
                    folderId
                });

                if (event.operationType === "create" || event.operationType === "update") {
                    if (!permissions.read.object) {
                        // Don't have permissions, so was reported for a different user, ignore
                        return;
                    }

                    const folderBeforeUpdate = this._state.resourceFolders.find(folder => folder.id === folderId);
                    if ((folderBeforeUpdate?.modifiedAt ?? 0) < workspaceResourceFolder.modifiedAt) {
                        this._updateState(state => ({
                            ...state,
                            resourceFolders: _.cloneDeep([...state.resourceFolders.filter(folder => folder.id !== folderId), workspaceResourceFolder])
                        }));
                    }
                } else if (event.operationType === "delete") {
                    if (permissions.read.object) {
                        // Still have permissions, so was reported for a different user, ignore
                        return;
                    }

                    const folderBeforeDelete = this._state.resourceFolders.find(folder => folder.id === folderId);
                    if ((folderBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                        this._updateState(state => ({
                            ...state,
                            resourceFolders: _.cloneDeep(state.resourceFolders.filter(folder => folder.id !== folderId))
                        }));
                    }
                }
            }

            if (event.documentType === "WorkspacePlan") {
                const plan = event.newDocument as IWorkspacePlan;
                this._updateState({ plan });
            }

            if (event.documentType === "WorkspaceUserInvite") {
                const inviteId = event.documentId;

                if (event.operationType === "create" || event.operationType === "update") {
                    const invite = event.newDocument as IWorkspaceUserInvite;
                    const inviteBeforeUpdate = this._state.userInvites.find(i => invite.id === inviteId);
                    if ((inviteBeforeUpdate?.modifiedAt ?? 0) < invite.modifiedAt) {
                        if (invite.acceptedAt) {
                            this._updateState(state => ({
                                ...state,
                                userInvites: _.cloneDeep(state.userInvites.filter(invite => invite.id !== inviteId))
                            }));
                        } else {
                            this._updateState(state => ({
                                ...state,
                                userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite])
                            }));
                        }
                    }
                } else if (event.operationType === "delete") {
                    const inviteBeforeDelete = this._state.userInvites.find(invite => invite.id === inviteId);
                    if ((inviteBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                        this._updateState(state => ({
                            ...state,
                            userInvites: _.cloneDeep(state.userInvites.filter(invite => invite.id !== inviteId))
                        }));
                    }
                }
            }

            if (event.documentType === "WorkspacePermission") {
                if (event.operationType === "delete") {
                    const workspacePermission = event.oldDocument as IWorkspacePermission;
                    const objectId = workspacePermission.objectId;
                    const objectType = workspacePermission.objectType;

                    if (objectType === WorkspacePermissionObjectType.RESOURCE_FOLDER) {
                        const folder = this.resourceFolders.find(folder => folder.id === objectId);
                        if (folder) {
                            await this.forceRefreshWorkspaceResourceFolders([folder.id], false);
                        }
                    }
                }
            }

            if (event.documentType === "WorkspaceUserInvite") {
                const inviteId = event.documentId;

                if (event.operationType === "create" || event.operationType === "update") {
                    const invite = event.newDocument;

                    const inviteBeforeUpdate = this._state.userInvites.find(invite => invite.id === inviteId);
                    if ((inviteBeforeUpdate?.modifiedAt ?? 0) < invite.modifiedAt) {
                        this._updateState(state => ({
                            ...state,
                            userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite].filter(invite => !invite.acceptedAt))
                        }));
                    }
                }
            }
        });
    }

    private async _onWorkspaceRoleChanged() {
        this._userModel.update({ workspaceRoleChange: null });
        await this._userModel.updatePromise.catch(err => {
            logger.error(err, "[WorkspaceController] _onWorkspaceRoleChanged() failed to clear workspaceRoleChange on user");
        });

        const role = this.legacyRole;
        const workspaceName = this._state.workspace.name;

        let message: string;
        switch (role) {
            case LegacyTeamMemberRole.OWNER:
                message = `Your role was changed to Owner in ${workspaceName}. Owners can manage members, Team Slides, and the Team Theme. Please refresh the browser to reflect the latest changes.`;
                break;
            case LegacyTeamMemberRole.LIBRARIAN:
                message = `Your role was changed to Librarian in ${workspaceName}. Librarians can manage Team Slides and the Team Theme. Please refresh the browser to reflect the latest changes.`;
                break;
            case LegacyTeamMemberRole.MEMBER_LICENSED:
                message = `Your role was changed to Pro Member in ${workspaceName}. Pro Members have access to Beautiful.ai Pro features. Please refresh the browser to reflect the latest changes.`;
                break;
            case LegacyTeamMemberRole.MEMBER:
                message = `Your role was changed to Free Team Member in ${workspaceName}. Free Team Members are limited to basic features. Please refresh the browser to reflect the latest changes.`;
                break;
        }

        ShowDialog(MandatoryUpdateDialog, { message });
    }

    private _emitResourcesChanged(params: Parameters<OnWorkspaceResourcesChangedCallback>[0]) {
        this._onWorkspaceResourcesChangedCallbacks.forEach(callback => callback(params));
    }

    public initialize(workspaceId: string, firebaseUser: User, userModel: IUserStorageModel) {
        return this._initialize(workspaceId, firebaseUser, userModel);
    }

    public reset(runSequentially = true) {
        return this._reset(runSequentially);
    }

    /**
    * Wraps the given component with a state injector that renders the component only if the controller
    * is initialized.
    *
    * !! THIS WRAPPER WILL *NOT* INITIALIZE THE CONTROLLER IF IT'S NOT ALREADY INITIALIZED !!
    */
    public withInitializedState<T extends React.ComponentType<any>>(Component: T, PreloadComponent: React.ComponentType<any> = Spinner, ErrorComponent: React.ComponentType<any> = () => null) {
        function Wrapper(props: React.ComponentProps<T>) {
            const { initialized, initializeError } = props;

            if (initializeError) {
                return <ErrorComponent {...props} />;
            }

            if (!initialized) {
                return <PreloadComponent {...props} />;
            }

            return <Component {...props} />;
        }

        return this.withState(Wrapper);
    }

    /**
     * Registers a callback to be called after the state of the WorkspaceController changes.
     */
    public onStateDidUpdate(callback: (prevState: WorkspaceControllerState, currentState: WorkspaceControllerState) => void) {
        this._stateDidUpdateCallbacks.push(callback);
        return () => {
            this._stateDidUpdateCallbacks = this._stateDidUpdateCallbacks.filter(cb => cb !== callback);
        };
    }

    /**
     * Registers a callback to be called when the resources of the workspace change.
     * Gets triggeed only upon resource-related calls to the controller, for Pusher events subscribe
     * to the channel instead.
     */
    public onWorkspaceResourcesChanged(callback: OnWorkspaceResourcesChangedCallback) {
        this._onWorkspaceResourcesChangedCallbacks.push(callback);
        return () => {
            this._onWorkspaceResourcesChangedCallbacks = this._onWorkspaceResourcesChangedCallbacks.filter(cb => cb !== callback);
        };
    }

    public getUsersWithAccessToResourceFolder(folderId: string) {
        const { resourceFolders } = this._state;

        const folder = resourceFolders.find(f => f.id === folderId);
        if (!folder) {
            throw new Error("Folder not found");
        }

        return this.getUsersInGroup(folder.userGroupId);
    }

    public isFolderSharedWithOthers(folder: Pick<IWorkspaceResourceFolder, "userGroupId">) {
        const { users, userGroups } = this._state;

        if (users.filter(user => user.groupIds.includes(folder.userGroupId)).length > 1) {
            return true;
        }

        // Skip RESOURCE_FOLDER groups — parent folder always links that way for subfolders.
        return userGroups.some(group =>
            group.id !== folder.userGroupId &&
            group.groupIds.includes(folder.userGroupId) &&
            group.type !== WorkspaceUserGroupType.RESOURCE_FOLDER
        );
    }

    public isFolderEffectivelySharedWithOthers(folder: Pick<IWorkspaceResourceFolder, "userGroupId">) {
        return this.isFolderSharedWithOthers(folder) || this.getUsersInGroup(folder.userGroupId).length > 1;
    }

    public getUsersInGroup(groupId: string) {
        const { users, userGroups } = this._state;
        const targetGroup = userGroups.find(group => group.id === groupId);

        // GUESTS is role-based rather than stored as a direct group membership.
        if (targetGroup?.type === WorkspaceUserGroupType.GUESTS) {
            return users.filter(user => user.role === WorkspaceUserRole.GUEST);
        }

        const groups = [
            groupId,
            ...userGroups.filter(group => group.groupIds.includes(groupId)).map(group => group.id)
        ];

        const getMoreGroups = () => userGroups.filter(group => !groups.includes(group.id) && group.groupIds.some(g => groups.includes(g)));
        while (getMoreGroups().length > 0) {
            groups.push(...getMoreGroups().map(group => group.id));
        }

        return users.filter(user => user.groupIds.some(g => groups.includes(g)));
    }

    /**
     * Checks if the workspace is past due and shows the appropriate dialog.
     * @returns true if a dialog was shown, false otherwise.
     */
    public async checkPastDue(reloadMetadata = false) {
        try {
            const user = this._userModel;

            if (reloadMetadata) {
                await this.forceRefreshWorkspace();
                await user.loadWorkspacesMetadata([this.workspaceId]);
            }

            await user.loadWorkspacesMetadataPromise;

            let metadata = user.workspacesMetadata.find(metadata => metadata.id === this.workspaceId);
            if (!metadata) {
                [metadata] = await getWorkspacesMetadata({ ids: [this.workspaceId] });
            }

            if (!metadata) {
                return false;
            }

            const {
                plan,
                forceLockWorkspace,
                accountManagerEmail,
                subscriptionChargeRequiresAction,
                subscriptionState,
                subscriptionHadTrial,
                subsctiptionTrialWasCanceled,
                subscriptionTrialFailed,
                subscriptionPaidCycles,
                subscriptionWasVoluntarilyCanceled,
                subscriptionLatestInvoicePaymentAttempts,
                planPreview
            } = metadata;

            const subscriptionStateType = subscriptionState?.type ?? null;

            // Enterprise
            if (plan.tier === WorkspacePlanTier.ENTERPRISE) {
                if (forceLockWorkspace || subscriptionStateType === "past_due" || subscriptionStateType === "terminal") {
                    reactMount(
                        <EnterpriseWorkspaceBlockedDialog
                            workspaceId={this.workspaceId}
                            isPastDue={subscriptionStateType === "past_due"}
                            forceLockWorkspace={forceLockWorkspace}
                            contactEmail={accountManagerEmail}
                            isCanceled={subscriptionStateType === "terminal"}
                            onClose={() => this._removeDialogContainer()}
                        />,
                        this._getDialogContainer(),
                        false
                    );
                    return true;
                }

                return false;
            }

            const canManageBilling = this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE];

            // Trial explicitly canceled
            // NOTE: we used to handle that via notification flow (WORKSPACE_USER_DOWNGRADED), now it's moved here so we have all
            // past due/cancelation logic in one place. If they have already seen the notification, the'll
            // get serverd the dialog again, that's expected behavior.
            if (subsctiptionTrialWasCanceled) {
                const messageKey = `trialCanceledDialog:${this.workspaceId}`;
                if (canManageBilling && !user.get("messagesSeen")?.[messageKey]) {
                    reactMount(
                        <TrialCanceledDialog
                            onClose={() => {
                                user.update({ messagesSeen: { ...user.get("messagesSeen"), [messageKey]: true } });
                                this._removeDialogContainer();
                            }}
                        />,
                        this._getDialogContainer(),
                        false
                    );
                    return true;
                }

                return false;
            }

            // Trial failed, meaning the first payment after trial failed (past_due -> canceled)
            if (subscriptionTrialFailed) {
                const messageKey = `trialFailedDialog:${this.workspaceId}`;
                if (canManageBilling && !user.get("messagesSeen")?.[messageKey]) {
                    reactMount(
                        <TrialFailedDialog
                            onClose={() => {
                                user.update({ messagesSeen: { ...user.get("messagesSeen"), [messageKey]: true } });
                                this._removeDialogContainer();
                            }}
                        />,
                        this._getDialogContainer(),
                        false
                    );
                    return true;
                }

                return false;
            }

            // Subscription canceled
            // NOTE: we used to handle that via notification flow (WORKSPACE_USER_DOWNGRADED), now it's moved here so we have all
            // past due/cancelation logic in one place. If they have already seen the notification, the'll
            // get serverd the dialog again, that's expected behavior.
            if (subscriptionStateType === "terminal") {
                const messageKey = `subscriptionCanceledDialog:${this.workspaceId}`;
                if (canManageBilling && !user.get("messagesSeen")?.[messageKey]) {
                    reactMount(
                        <SubscriptionCanceledDialog
                            voluntaryCancellation={subscriptionWasVoluntarilyCanceled}
                            onClose={() => {
                                user.update({ messagesSeen: { ...user.get("messagesSeen"), [messageKey]: true } });
                                this._removeDialogContainer();
                            }}
                        />,
                        this._getDialogContainer(),
                        false
                    );
                    return true;
                }

                return false;
            }

            // Plan preview
            if (plan.tier === WorkspacePlanTier.PRO && plan.type === WorkspacePlanType.PRO_PREVIEW && planPreview?.enabled && planPreview.activeTill < Date.now()) {
                reactMount(
                    <PlanPreviewEndedDialog onClose={() => this._removeDialogContainer()} canManageBilling={canManageBilling} />,
                    this._getDialogContainer(),
                    false
                );
                return true;
            }

            // Not dunning
            if (!subscriptionChargeRequiresAction && subscriptionStateType !== "past_due") {
                return false;
            }

            const confirmCharge = () => new Promise(resolve =>
                reactMount(
                    <ConfirmSubscriptionChargeDialog
                        onClose={success => {
                            this._removeDialogContainer();
                            resolve(success);
                        }}
                    />,
                    this._getDialogContainer(),
                    false
                )
            );

            // Has paid cycles or didn't have a trial -> non-blocking experience
            if (subscriptionPaidCycles > 0 || !subscriptionHadTrial) {
                // Allow using while in dunning for regular members
                if (!this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]) {
                    return false;
                }

                // Try confirming charge first
                if (subscriptionChargeRequiresAction) {
                    const confirmed = await confirmCharge();
                    if (confirmed) {
                        return true;
                    }
                }

                // Show non-blocking payment issue dialog
                reactMount(
                    <PaymentIssueDialog
                        onClose={() => this._removeDialogContainer()}
                    />,
                    this._getDialogContainer(),
                    false
                );
                return true;
            }

            // Had trial and no paid cycles
            {
                const lockWorkspace = subscriptionLatestInvoicePaymentAttempts >= 3;

                // Locked workspace dialog for members (doesn't allow managing billing)
                if (!this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]) {
                    reactMount(
                        <TrialPausedDialogForMember
                            lockWorkspace={lockWorkspace}
                            onClose={() => this._removeDialogContainer()}
                        />,
                        this._getDialogContainer(),
                        false
                    );
                    return true;
                }

                // Try confirming charge
                if (subscriptionChargeRequiresAction) {
                    const confirmed = await confirmCharge();
                    if (confirmed) {
                        return true;
                    }
                }

                // Show trial paused dialog
                reactMount(
                    <TrialPausedDialog
                        subscriptionLatestInvoicePaymentAttempts={subscriptionLatestInvoicePaymentAttempts}
                        lockWorkspace={lockWorkspace}
                        onClose={() => this._removeDialogContainer()}
                    />,
                    this._getDialogContainer(),
                    false
                );

                return true;
            }
        } catch (err) {
            logger.error(err, "[WorkspaceController] checkPastDue() failed");
            return false;
        }
    }

    /**
     * Determines if user's account can be upgraded (either their role or the workspace plan) to remove a restriction on a specific action.
     *
     * Used for determining if an upgrade CTA should be shown. The logic is adjusted to be as close as possible
     * to the legacy logic for determining if an upgrade CTA should be shown.
     *
     * Examples:
     * - User is guest and the action is not allowed for them, but the workspace plan allows the action, meaning upgrading to a paid member will remove the restriction (true is returned)
     * - User is not guest, and the action is restricted by the plan, meaning the restriction can be lifted by upgrading the workspace plan (true is returned)
     */
    public canUpgradeToRemoveActionRestriction(actionObject: ActionPermissionsObject, actionType: PermissionActionType) {
        if (this.actionPermissions[actionObject][actionType]) {
            // Already unrestricted
            return false;
        }

        if (this.role === WorkspaceUserRole.GUEST && this.planPermissions[actionObject]) {
            // Guest, means access is restricted by them being a guest,
            // so they can be upgraded to workspace member
            return true;
        }

        if (!this.planPermissions[actionObject]) {
            // User is not guest, but the action is restricted by the plan
            // so they can be upgraded
            return true;
        }

        return false;
    }

    public async forceRefreshWorkspace() {
        return this._runSequentially(async () => {
            const [workspace, plan] = await Promise.all([
                workspacesApi.getWorkspace({ workspaceId: this.workspaceId }),
                workspacesBillingApi.getWorkspacePlan({ workspaceId: this.workspaceId })
            ]);
            await this._updateState({ workspace, plan });
        });
    }

    public async forceRefreshWorkspaceResourceFolders(folderIds?: string[], runSequentially = true) {
        const refresh = async () => {
            const workspaceResourceFolders = await workspaceResourceFoldersApi.getWorkspaceResourceFolders({ workspaceId: this.workspaceId, folderIds });
            await this._updateState(state => ({
                ...state,
                resourceFolders: folderIds ? _.cloneDeep([...state.resourceFolders.filter(folder => !folderIds.includes(folder.id)), ...workspaceResourceFolders]) : workspaceResourceFolders
            }));
        };

        if (runSequentially) {
            return this._runSequentially(refresh);
        }

        return refresh();
    }

    public async forceRefreshWorkspaceUserGroups(groupIds?: string[], runSequentially = true) {
        const refresh = async () => {
            const userGroups = await workspaceUserGroupsApi.getWorkspaceUserGroups({ workspaceId: this.workspaceId, groupIds });
            await this._updateState(state => ({
                ...state,
                userGroups: groupIds ? _.cloneDeep([...state.userGroups.filter(group => !groupIds.includes(group.id)), ...userGroups]) : userGroups
            }));
        };

        if (runSequentially) {
            return this._runSequentially(refresh);
        }

        return refresh();
    }

    public async forceRefreshWorkspaceUsers(uids?: string[], runSequentially = true) {
        const refresh = async () => {
            const users = await workspaceUsersApi.getWorkspaceUsers({ workspaceId: this.workspaceId, uids });
            await this._updateState(state => ({
                ...state,
                users: uids
                    ? _.cloneDeep([...state.users.filter(user => !uids.includes(user.uid)), ...users])
                    : users
            }));
        };

        if (runSequentially) {
            return this._runSequentially(refresh);
        }

        return refresh();
    }

    public async updateWorkspace(update: UpdateWorkspaceRequest["update"]) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.updateWorkspace({ workspaceId: this.workspaceId, update });

            //// Legacy analytics ////
            if (update.name) {
                const props = {
                    old_workspace_name: workspace.name,
                    new_workspace_name: update.name
                };
                trackState({ "workspace_name": update.name });
                trackActivity("Organization", "NameChanged", null, null, props, { audit: true });
            }
            ////

            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async updateWorkspaceUserGroups({ update }: {
        update: {
            [groupId: string]: {
                actionPermissions: Partial<IWorkspaceUserGroup["actionPermissions"]>;
            };
        }
    }) {
        return this._runSequentially(async () => {
            const userGroups = await workspaceUserGroupsApi.updateWorkspaceUserGroups({ workspaceId: this.workspaceId, update });

            trackActivity("OrgTeam", "PermissionsChanged", null, null, {
                workspace_id: this.workspaceId,
                group_ids: Object.keys(update)
            }, { audit: true });

            await this._updateState(state => ({
                ...state,
                userGroups: _.cloneDeep([...state.userGroups.filter(group => !userGroups.some(ug => ug.id === group.id)), ...userGroups])
            }));
            return userGroups;
        });
    }

    public async createCustomWorkspaceUserGroup(name: string) {
        return this._runSequentially(async () => {
            const group = await workspaceUserGroupsApi.createCustomWorkspaceUserGroup({
                workspaceId: this.workspaceId,
                name
            });
            await this._updateState(state => ({
                ...state,
                userGroups: _.cloneDeep([...state.userGroups.filter(existing => existing.id !== group.id), group])
            }));
            return group;
        });
    }

    public async renameCustomWorkspaceUserGroup(customGroupId: string, name: string) {
        return this._runSequentially(async () => {
            const group = await workspaceUserGroupsApi.renameCustomWorkspaceUserGroup({
                workspaceId: this.workspaceId,
                customGroupId,
                name
            });
            await this._updateState(state => ({
                ...state,
                userGroups: _.cloneDeep([...state.userGroups.filter(existing => existing.id !== group.id), group])
            }));
            return group;
        });
    }

    public async deleteCustomWorkspaceUserGroup(customGroupId: string) {
        return this._runSequentially(async () => {
            await workspaceUserGroupsApi.deleteCustomWorkspaceUserGroup({
                workspaceId: this.workspaceId,
                customGroupId
            });
            await this._updateState(state => ({
                ...state,
                userGroups: _.cloneDeep(state.userGroups.filter(group => group.id !== customGroupId)),
                users: _.cloneDeep(state.users.map(user => ({
                    ...user,
                    groupIds: user.groupIds.filter(groupId => groupId !== customGroupId)
                })))
            }));
        });
    }

    public async updateCustomWorkspaceUserGroupUsers(customGroupId: string, update: { add?: string[]; remove?: string[] }) {
        return this._runSequentially(async () => {
            const uids = [...new Set([...(update.add ?? []), ...(update.remove ?? [])])];
            const { users, userGroups } = await workspaceUserGroupsApi.updateCustomWorkspaceUserGroupUsers({
                workspaceId: this.workspaceId,
                customGroupId,
                update
            });
            await this._updateState(state => ({
                ...state,
                users: _.cloneDeep([...state.users.filter(user => !uids.includes(user.uid)), ...users]),
                userGroups: _.cloneDeep([...state.userGroups.filter(group => group.id !== customGroupId), ...userGroups])
            }));
        });
    }

    public async updateWorkspaceResourceFolder(request: Omit<Parameters<typeof workspaceResourceFoldersApi.updateWorkspaceResourceFolder>[0], "workspaceId">) {
        return this._runSequentially(async () => {
            const previousFolder = this.resourceFolders.find(folder => folder.id === request.folderId);
            const workspaceResourceFolder = await workspaceResourceFoldersApi.updateWorkspaceResourceFolder({ ...request, workspaceId: this.workspaceId });

            trackActivity("Folder", "Updated", null, null, {
                workspace_id: this.workspaceId,
                folder_id: request.folderId,
                old_folder_name: previousFolder?.name,
                new_folder_name: workspaceResourceFolder?.name
            }, { audit: true });

            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep([...state.resourceFolders.filter(folder => folder.id !== request.folderId), workspaceResourceFolder])
            }));
            return workspaceResourceFolder;
        });
    }

    public async deleteWorkspaceResourceFolder({ folderId }: { folderId: string }) {
        return this._runSequentially(async () => {
            const folder = this.resourceFolders.find(resourceFolder => resourceFolder.id === folderId);
            await workspaceResourceFoldersApi.deleteWorkspaceResourceFolder({ workspaceId: this.workspaceId, folderId });

            trackActivity("Folder", "Deleted", null, null, {
                workspace_id: this.workspaceId,
                folder_id: folderId,
                folder_name: folder?.name
            }, { audit: true });

            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep(state.resourceFolders.filter(folder => folder.id !== folderId))
            }));
        });
    }

    public async createWorkspaceResourceFolder(request: Omit<Parameters<typeof workspaceResourceFoldersApi.createWorkspaceResourceFolder>[0], "workspaceId">) {
        return this._runSequentially(async () => {
            const { folder, userGroups, users } = await workspaceResourceFoldersApi.createWorkspaceResourceFolder({ workspaceId: this.workspaceId, ...request });
            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep([...state.resourceFolders, folder]),
                userGroups: _.cloneDeep([...state.userGroups.filter(group => !userGroups.some(ug => ug.id === group.id)), ...userGroups]),
                users: _.cloneDeep([...state.users.filter(user => !users.some(u => u.uid === user.uid)), ...users])
            }));

            //// Legacy analytics ////
            if (request.folderType === WorkspaceResourceFolderType.PERSONAL) {
                trackActivity("Library", "NewFolder", null, null, { folder_id: folder.id, folder_name: folder.name }, { audit: false });
            } else {
                const props = {
                    workspace_id: this.workspaceId,
                    folder_id: folder.id,
                    folder_name: folder.name
                };
                trackActivity("OrgTeam", "Created", null, null, props, { audit: true });
            }
            ////

            return folder;
        });
    }

    public async createWorkspaceResourceFolderAndShareWithDefaultGroups(request: Omit<Parameters<typeof workspaceResourceFoldersApi.createWorkspaceResourceFolder>[0], "workspaceId" | "permissions">) {
        const { userGroups } = this._state;

        const groups = userGroups.filter(group => [WorkspaceUserGroupType.MEMBERS, WorkspaceUserGroupType.LIBRARIANS, WorkspaceUserGroupType.OWNERS].includes(group.type));
        return this.createWorkspaceResourceFolder({
            ...request,
            permissions: groups.map(group => ({
                subjectId: group.id,
                subjectType: WorkspacePermissionSubjectType.USER_GROUP,
            })),
        });
    }

    public async updateWorkspaceResourceFolderResources(request: Omit<Parameters<typeof workspaceResourceFoldersApi.updateWorkspaceResourceFolderResources>[0], "workspaceId">) {
        const workspaceResources = await workspaceResourceFoldersApi.updateWorkspaceResourceFolderResources({ workspaceId: this.workspaceId, ...request });

        //// Legacy analytics ////
        const add = request.update.add ?? [];
        if (add.length > 0) {
            trackActivity("Folder", "PresentationsAdded", null, null, { ids: add.filter(r => r.resourceType === WorkspaceResourceType.PRESENTATION).map(r => r.resourceId) }, { audit: true });
        }

        const remove = request.update.remove ?? [];
        for (const resource of remove.filter(r => r.resourceType === WorkspaceResourceType.PRESENTATION)) {
            trackActivity("Folder", "PresentationRemoved", null, null, { presentation_id: resource.resourceId }, { audit: true });
        }
        ////

        this._emitResourcesChanged({
            added: [],
            removed: [],
            updated: workspaceResources
        });

        return workspaceResources;
    }

    public async updateWorkspaceResource(updates: Omit<Parameters<typeof workspaceResourcesApi.updateWorkspaceResource>[0], "workspaceId">, analyticsProps?: { isNewAsset?: boolean, isReplacedAsset?: boolean }) {
        const workspaceResource = await workspaceResourcesApi.updateWorkspaceResource({ ...updates, workspaceId: this.workspaceId } as Parameters<typeof workspaceResourcesApi.updateWorkspaceResource>[0]);

        //// Legacy analytics ////
        if (updates.resourceType === WorkspaceResourceType.ASSET) {
            const metadata = updates.metadata as IWorkspaceResourceAsset["metadata"];
            trackActivity("TeamAsset", "Updated", null, null, {
                id: updates.resourceId,
                workspace_id: this.workspaceId,
                type: metadata?.type,
            }, { audit: true });

            if (analyticsProps?.isReplacedAsset) {
                trackActivity("TeamAsset", "Replaced", null, null, {
                    id: updates.resourceId,
                    workspace_id: this.workspaceId,
                    type: metadata?.type,
                    isNew: analyticsProps?.isNewAsset
                }, { audit: true });
            }
        }
        ////

        this._emitResourcesChanged({
            added: [],
            removed: [],
            updated: [workspaceResource]
        });

        return workspaceResource;
    }

    public async updateSharedThemesOrder(resourceIds: string[]) {
        const workspaceResources = await workspaceResourcesApi.updateSharedThemesOrder({ workspaceId: this.workspaceId, resourceIds });

        this._emitResourcesChanged({
            added: [],
            removed: [],
            updated: workspaceResources
        });

        return workspaceResources;
    }

    public async updateWorkspaceResourceRating(request: Omit<Parameters<typeof workspaceResourcesApi.updateWorkspaceResourceRating>[0], "workspaceId">) {
        const apiRequest: Parameters<typeof workspaceResourcesApi.updateWorkspaceResourceRating>[0] = {
            ...request,
            workspaceId: this.workspaceId
        };
        const workspaceResource = await workspaceResourcesApi.updateWorkspaceResourceRating(apiRequest);

        this._emitResourcesChanged({
            added: [],
            removed: [],
            updated: [workspaceResource]
        });

        return workspaceResource;
    }

    public async deleteWorkspaceResource(request: Omit<Parameters<typeof workspaceResourcesApi.deleteWorkspaceResource>[0], "workspaceId">, analyticsProps?: { trackActivity: string }) {
        const [resourceBefore] = await workspaceResourcesApi.getWorkspaceResources({
            workspaceId: this.workspaceId,
            resourceTypes: [request.resourceType],
            resourceFilters: [{ field: "resourceId", operator: "eq", value: request.resourceId }]
        });
        if (!resourceBefore) {
            throw new Error("Resource not found");
        }

        await workspaceResourcesApi.deleteWorkspaceResource({ ...request, workspaceId: this.workspaceId });

        //// Legacy analytics ////
        if (request.resourceType === WorkspaceResourceType.SLIDE || request.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE) {
            const props = {
                slide_id: request.resourceId,
            };
            trackActivity(analyticsProps?.trackActivity ?? "SharedSlideLibrary", "SlideDeleted", null, null, props, { audit: true });
        }
        if (request.resourceType === WorkspaceResourceType.ASSET) {
            const metadata = resourceBefore.metadata as IWorkspaceResourceAsset["metadata"];
            trackActivity("TeamAsset", "Deleted", null, null, {
                id: request.resourceId,
                workspace_id: this.workspaceId,
                type: metadata?.type
            }, { audit: true });
        }
        ////

        this._emitResourcesChanged({
            added: [],
            removed: [{ resourceId: request.resourceId, resourceType: request.resourceType }],
            updated: []
        });
    }

    public async createWorkspaceResource(request: Omit<Parameters<typeof workspaceResourcesApi.createWorkspaceResource>[0], "workspaceId">, analyticsProps?: { template_name?: string, isNewAsset?: boolean, assetType?: string }) {
        return this._runSequentially(async () => {
            const workspaceResource = await workspaceResourcesApi.createWorkspaceResource({ ...request, workspaceId: this.workspaceId });

            //// Legacy analytics ////
            const resource = request.resource;
            if (resource.resourceType === WorkspaceResourceType.SLIDE || resource.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE) {
                const props = {
                    slide_id: resource.resourceId,
                    template_name: analyticsProps?.template_name,
                };
                trackActivity("SharedSlideLibrary", "SlideAdded", null, null, props, { audit: true });
            }
            if (resource.resourceType === WorkspaceResourceType.ASSET) {
                trackActivity("TeamAsset", "Added", null, null, {
                    id: resource.resourceId,
                    workspace_id: this.workspace.id,
                    type: analyticsProps?.assetType,
                    isNewAsset: analyticsProps?.isNewAsset,
                }, { audit: true });
            }
            if (resource.resourceType === WorkspaceResourceType.PRESENTATION_TEMPLATE) {
                trackActivity("PresentationTemplates", "PresentationTemplateAdded", null, null, {
                    id: resource.resourceId
                }, { audit: true });
            }
            ////

            this._emitResourcesChanged({
                added: [workspaceResource],
                removed: [],
                updated: []
            });

            return workspaceResource;
        });
    }

    public async verifyDomain(domain: string) {
        return this._runSequentially(async () => {
            const { workspace, isDomainVerified, domainVerificationError } = await workspaceIntegrationsApi.verifyWorkspaceDomain({ workspaceId: this.workspaceId, domain });
            await this._updateState({ workspace });
            return { workspace, isDomainVerified, domainVerificationError };
        });
    }

    public async removeDomain(domain: string) {
        return this._runSequentially(async () => {
            const workspace = await workspaceIntegrationsApi.removeWorkspaceDomain({ workspaceId: this.workspaceId, domain });
            trackActivity("Organization", "DomainRemoved", null, null, { workspace_id: this.workspaceId, domain }, { audit: true });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async addDomain(domain: string) {
        return this._runSequentially(async () => {
            const workspace = await workspaceIntegrationsApi.addWorkspaceDomain({ workspaceId: this.workspaceId, domain });
            trackActivity("Organization", "DomainAdded", null, null, { workspace_id: this.workspaceId, domain }, { audit: true });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async parseSamlMetadata({ metadataUrl }: { metadataUrl: string }) {
        return this._runSequentially(async () => {
            const workspace = await workspaceIntegrationsApi.parseSamlMetadata({ workspaceId: this.workspaceId, metadataUrl });
            trackActivity("Organization", "SamlMetadataUpdated", null, null, { workspace_id: this.workspaceId, metadata_url: metadataUrl }, { audit: true });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async disconnectWorkspaceIntegration({ integrationType }: { integrationType: WorkspaceIntegrationType }) {
        return this._runSequentially(async () => {
            const workspace = await workspaceIntegrationsApi.disconnectWorkspaceIntegration({ workspaceId: this.workspaceId, integrationType });
            trackActivity("Integration", "Disconnected", null, null, { workspace_id: this.workspaceId, integration_type: integrationType }, { audit: true });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async connectWorkspaceIntegration({ integrationType, config }: { integrationType: WorkspaceIntegrationType, config: IWorkspaceIntegrationConfig }) {
        return this._runSequentially(async () => {
            const workspace = await workspaceIntegrationsApi.connectWorkspaceIntegration({ workspaceId: this.workspaceId, integrationType, config });
            trackActivity("Integration", "Connected", null, null, { workspace_id: this.workspaceId, integration_type: integrationType }, { audit: true });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async downloadUsersAndInvitesList() {
        const { users, userInvites } = this._state;

        const lines = [
            "Member,Email,Role,Pending,License,Last Active",
            ...[
                ...users.map(user => ({ ...user, pending: false, displayName: user.displayName ?? user.email })),
                ...userInvites.map(invite => ({ ...invite, pending: true, lastActive: null, displayName: invite.email }))
            ].map(member => ([
                member.displayName,
                member.email,
                getLegacyRoleFromWorkspaceUserRole(member.role),
                member.pending ? "Pending Invite" : "",
                member.role === WorkspaceUserRole.GUEST ? "free" : "team pro",
                member.lastActive ? moment(member.lastActive).format("YYYY-MM-DD HH:mm") : ""
            ].join(",")))
        ];

        download(`data:text/plain;charset=utf-8,${encodeURIComponent(lines.join("\n"))}`, `members.csv`);
    }

    public async resendUserInvite({ inviteId }: { inviteId: string }) {
        return this._runSequentially(async () => {
            const invite = await workspaceUserInvitesApi.resendWorkspaceUserInvite({ workspaceId: this.workspaceId, inviteId });

            //// Legacy analytics ////
            trackActivity("Organization", "ResendTeamInvite", null, null, {
                workspace_id: this.workspaceId,
                license: invite.role === WorkspaceUserRole.GUEST ? "free" : "pro",
                recipient_email: invite.email,
                sender_email: this._firebaseUser.email,
            }, { audit: true });
            ////

            await this._updateState(state => ({
                ...state,
                userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite])
            }));
        });
    }

    public async copyUserInviteLinkToClipboard({ invite }: { invite: IWorkspaceUserInvite }) {
        return this._runSequentially(async () => {
            const url = await workspaceUserInvitesApi.getWorkspaceUserInviteUrl({
                workspaceId: invite.workspaceId,
                inviteId: invite.id
            });

            await clipboardWrite({
                [ClipboardType.TEXT]: url,
            });

            //// Legacy analytics ////
            const props = {
                workspace_id: invite.workspaceId,
                license: invite.role === WorkspaceUserRole.GUEST ? "free" : "pro",
                recipient_email: invite.email,
                sender_email: this._firebaseUser.email,
                teamInviteUrl: url,
            };
            trackActivity("Organization", "CopyTeamInviteLink", null, null, props, { audit: true });
            ////
        });
    }

    public async deleteUserInvite({ inviteId }: { inviteId: string }) {
        return this._runSequentially(async () => {
            const deletedInvite = await workspaceUserInvitesApi.deleteWorkspaceUserInvite({ workspaceId: this.workspaceId, inviteId });

            //// Legacy analytics ////
            trackActivity("Organization", "InviteRemoved", null, null, { email: deletedInvite.email, workspace_id: this.workspaceId }, { audit: true });
            ////

            await this._updateState(state => ({
                ...state,
                userInvites: _.cloneDeep(state.userInvites.filter(invite => invite.id !== inviteId))
            }));
        });
    }

    public async updateUserInvite({ inviteId, update }: { inviteId: string, update: Pick<IWorkspaceUserInvite, "role"> }) {
        return this._runSequentially(async () => {
            const existingInvite = this.userInvites.find(invite => invite.id === inviteId);
            const invite = await workspaceUserInvitesApi.updateWorkspaceUserInvite({ workspaceId: this.workspaceId, inviteId, update });

            trackActivity("Organization", "InviteUpdated", null, null, {
                workspace_id: this.workspaceId,
                invite_id: inviteId,
                email: invite.email,
                old_role: existingInvite?.role,
                new_role: invite.role
            }, { audit: true });

            await this._updateState(state => ({
                ...state,
                userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite])
            }));
        });
    }

    public async inviteWorkspaceUsers(users: Pick<IWorkspaceUserInvite, "email" | "role">[], analyticsProps?: { [key: string]: any }) {
        return this._runSequentially(async () => {
            const userInvites = await workspaceUserInvitesApi.inviteWorkspaceUsers({ workspaceId: this.workspaceId, users });
            _trackInviteWorkspaceUsers(this.workspaceId, users, analyticsProps);
            Analytics.ModifyWorkspaceUsers.InviteUsersComplete({
                quantity_invites_sent: userInvites.length,
                quantity_new_seats_added: analyticsProps?.quantity_new_seats_added ?? 0,
                invite_ids: userInvites.map(invite => invite.id),
            });
            await this._updateState(state => ({ ...state, userInvites: _.cloneDeep([...state.userInvites.filter(invite => !userInvites.some(u => u.id === invite.id)), ...userInvites]) }));
        });
    }

    public async updateWorkspaceUser({ uid, update }: { uid: string, update: Pick<IWorkspaceUser, "role"> }) {
        return this._runSequentially(async () => {
            const existingUser = this.users.find(user => user.uid === uid);
            const user = await workspaceUsersApi.updateWorkspaceUser({ workspaceId: this.workspaceId, uid, update });

            trackActivity("OrgTeam", "MembershipChanged", null, null, {
                workspace_id: this.workspaceId,
                user_id: uid,
                email: user.email,
                old_role: existingUser?.role ? getLegacyRoleFromWorkspaceUserRole(existingUser.role) : null,
                new_role: getLegacyRoleFromWorkspaceUserRole(user.role)
            }, { audit: true });

            await this._updateState(state => ({ ...state, users: _.cloneDeep([...state.users.filter(user => user.uid !== uid), user]) }));
        });
    }

    public async deleteWorkspaceUser({ uid, transferOption, targetUid }: { uid: string, transferOption: "other" | "revoke" | "self", targetUid?: string }) {
        return this._runSequentially(async () => {
            const deletedUser = await deleteWorkspaceUser({ workspaceId: this.workspaceId, uid, transferOption, targetUid });

            await this._updateState(state => ({ ...state, users: _.cloneDeep(state.users.filter(user => user.uid !== uid)) }));

            return deletedUser;
        });
    }

    public async updateWorkspaceResourceUsers(request: Omit<Parameters<typeof workspaceResourcesApi.updateWorkspaceResourceUsers>[0], "workspaceId">) {
        const { invites, resource, permissions, users } = await workspaceResourcesApi.updateWorkspaceResourceUsers({
            ...request,
            workspaceId: this.workspaceId,
        });

        await this._updateState(state => ({
            ...state,
            users: _.cloneDeep([...state.users.filter(user => !users.some(u => u.uid === user.uid)), ...users])
        }));

        this._emitResourcesChanged({
            added: [],
            removed: [],
            updated: [resource]
        });

        return { invites, resource, permissions, users };
    }

    public async updateWorkspaceResourceFolderUsersAndGroups(request: Omit<Parameters<typeof workspaceResourceFoldersApi.updateWorkspaceResourceFolderUsersAndGroups>[0], "workspaceId">) {
        const resourcesInFolderBefore = await workspaceResourcesApi.getWorkspaceResources({
            workspaceId: this.workspaceId,
            resourceTypes: Object.values(WorkspaceResourceType),
            resourceFilters: [{ field: "folderIds", operator: "eq", value: request.folderId }]
        });

        const { userGroups, users, resourceFolder } = await workspaceResourceFoldersApi.updateWorkspaceResourceFolderUsersAndGroups({ workspaceId: this.workspaceId, ...request });

        const resourcesInFolderAfter = await workspaceResourcesApi.getWorkspaceResources({
            workspaceId: this.workspaceId,
            resourceTypes: Object.values(WorkspaceResourceType),
            resourceFilters: [{ field: "folderIds", operator: "eq", value: request.folderId }]
        });

        await this._updateState(state => ({
            ...state,
            userGroups: _.cloneDeep([...state.userGroups.filter(group => !userGroups.some(g => g.id === group.id)), ...userGroups]),
            users: _.cloneDeep([...state.users.filter(user => !users.some(u => u.uid === user.uid)), ...users]),
            resourceFolders: _.cloneDeep([...state.resourceFolders.filter(folder => folder.id !== request.folderId), resourceFolder]).filter(Boolean)
        }));

        const addedResources = resourcesInFolderAfter.filter(resource => !resourcesInFolderBefore.some(r => r.id === resource.id));
        const removedResources = resourcesInFolderBefore.filter(resource => !resourcesInFolderAfter.some(r => r.id === resource.id));
        const updatedResources = resourcesInFolderAfter.filter(resource => !removedResources.some(r => r.id === resource.id) && !addedResources.some(r => r.id === resource.id));

        this._emitResourcesChanged({
            added: addedResources,
            removed: removedResources,
            updated: updatedResources
        });

        //// Legacy analytics ////
        try {
            const workspaceId = this.workspaceId;
            const { folderId, update } = request;

            if (resourceFolder && ("add" in update || "remove" in update)) {
                if ("add" in update) {
                    const addedUids = update.add.filter(p => p.subjectType === WorkspacePermissionSubjectType.USER).map(p => p.subjectId);

                    const props = {
                        workspace_id: workspaceId,
                        workspace_name: this.workspace.name,
                        folder_id: folderId,
                        folder_name: resourceFolder.name,
                        userid_added: addedUids,
                        userid_removed: null,
                        num_team_members: users.length,
                        num_changed: addedUids.length,
                        change_mechanism: "admin initiated"
                    };
                    trackActivity("OrgTeam", "MembershipChanged", null, null, props, { audit: true });
                }

                if ("remove" in update) {
                    const removedUids = update.remove.filter(p => p.subjectType === WorkspacePermissionSubjectType.USER).map(p => p.subjectId);

                    const props = {
                        workspace_id: workspaceId,
                        workspace_name: this.workspace.name,
                        folder_id: folderId,
                        folder_name: resourceFolder.name,
                        userid_added: null,
                        userid_removed: removedUids,
                        num_team_members: users.length,
                        num_changed: removedUids.length,
                        change_mechanism: removedUids.includes(auth().currentUser.uid) ? "user initiated" : "admin initiated"
                    };
                    trackActivity("OrgTeam", "MembershipChanged", null, null, props, { audit: true });
                }
            }
        } catch (err) {
            logger.error(err, "[WorkspaceController] updateWorkspaceResourceFolderUsersAndGroups() failed to track analytics");
        }
        ////
    }

    public async printWorkspaceDebugData() {
        try {
            const { url } = await debugApi.getWorkspaceDebugData({ workspaceId: this.workspaceId });

            if (!url) {
                logger.info("[WorkspaceController] printWorkspaceDebugData() no data");
                return;
            }

            window.open(`/admin/debug?url=${encodeURIComponent(url)}`, "_blank");
        } catch (err) {
            logger.error(err, "[WorkspaceController] printWorkspaceDebugData() failed");
        }
    }

    /**
     * Exports all presentations in the workspace to the specified asset type.
     * Not supposed to be exposed in the UI, only for CS use.
     */
    public async exportPresentations({
        assetType = "pdf",
        includeSkippedSlides = false,
    }: {
        assetType?: "pdf" | "pptx" | "pptx-editable" | "zip";
        includeSkippedSlides?: boolean;
    } = {}) {
        if (assetType === "pptx-editable") {
            const canExportEditable = this.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE].use;
            if (!canExportEditable) {
                ShowWarningDialog({
                    title: "Not allowed",
                    message: "You are not allowed to export editable PowerPoint presentations",
                });
                return;
            }
        }

        const records: IWorkspaceResourceWithDocument<IPresentation, IWorkspaceResourcePresentation>[] = [];

        let offset = 0;
        while (true) {
            const { items, total } = await getWorkspaceResourcesWithDocuments({
                workspaceId: this.workspaceId,
                resourceTypes: [WorkspaceResourceType.PRESENTATION],
                offset,
                size: 100,
                sort: [{ field: "resource.createdAt", direction: "desc" }],
                filters: [],
                documentFields: ["id", "name"]
            }) as PaginatedResponse<IWorkspaceResourceWithDocument<IPresentation, IWorkspaceResourcePresentation>>;
            records.push(...items);
            if (total <= offset + 100) {
                break;
            }
            offset += 100;
        }

        if (records.length === 0) {
            ShowWarningDialog({
                title: "No presentations found",
                message: "Nothing to export",
                acceptCallback: () => { }
            });
            return;
        }

        const pptxMode = assetType === "pptx-editable" ? "editable" : "static";
        assetType = assetType === "pptx-editable" ? "pptx" : assetType;

        const exportPresentation = (presentationId: string) => new Promise<string>((resolve, reject) => {
            const onTaskChanged = (task: { state: string, signedUrl: string, errorMessage: string, stateProgressPercents: number }) => {
                if (task.state === TaskState.ERROR) {
                    reject(new Error(`Export failed with message: ${task.errorMessage}`));
                    return;
                }

                if (task.state === TaskState.FINISHED) {
                    resolve(task.signedUrl);
                    return;
                }
            };

            const taskProps = {
                presentationId,
                assetType,
                pptxMode,
                forPrinting: false,
                includeSkippedSlides,
                pdfCompressionType: "none"
            };

            requestExportCache(onTaskChanged, taskProps)
                .catch(reject);
        });

        let shouldCancel = false;
        const progressDialog = ShowDialog(ProgressDialog, {
            title: `Exporting ${records.length} presentations`,
            onCancel: () => {
                shouldCancel = true;
                progressDialog.setTitle("Cancelling export...");
            }
        });

        let exportedCount = 0;
        let failedCount = 0;
        await withMaxConcurrency(records.map(record => async () => {
            if (shouldCancel) {
                return;
            }

            try {
                const signedUrl = await exportPresentation(record.document.id);
                if (shouldCancel) {
                    return;
                }
                const fileName = filenameForExport({ name: record.document.name, assetType });
                await downloadFromUrl(signedUrl, fileName);
                exportedCount++;
                progressDialog.setProgress(exportedCount / records.length * 100);
            } catch (err) {
                failedCount++;
                logger.error(err, "WorkspaceController exportPresentations() failed", { presentationId: record.document.id });
            }
        }), 10);

        progressDialog.props.closeDialog();

        ShowMessageDialog({
            title: "Export complete",
            message: `Exported ${exportedCount} presentations.${failedCount ? ` Failed to export ${failedCount} presentations.` : ""}${shouldCancel ? "  Export cancelled." : ""}`,
            acceptCallback: () => { }
        });
    }
}

const workspaceController = new WorkspaceController();

// for lazy import and debug
app.workspaceController = workspaceController;

export default workspaceController;

async function _getAnalyticsProps(workspaceMetadata: IWorkspaceMetadata) {
    return {
        workspace_id: workspaceMetadata.id,
        workspace_type: "workspace",
        workspace_name: workspaceMetadata.name,
        workspace_plan: workspaceMetadata.plan.name,
        current_slide_count: await getSlidesInPresentationsCount(workspaceMetadata.id).catch(() => 0),
        role: workspaceMetadata.userRole
    };
}

function _trackInviteWorkspaceUsers(workspaceId: string, users: Pick<IWorkspaceUserInvite, "role" | "email">[], analyticsProps?: { [key: string]: any }) {
    const inviteProps = {
        workspace_id: workspaceId,
        recipients: users.map(u => u.email),
        user_roles: users.map(u => {
            if (u.role === WorkspaceUserRole.GUEST) {
                return AnalyticsRolesAndLicensesMap["free"];
            }
            if (u.role === WorkspaceUserRole.LIBRARIAN) {
                return AnalyticsRolesAndLicensesMap["librarian"];
            }
            if (u.role === WorkspaceUserRole.MEMBER) {
                return AnalyticsRolesAndLicensesMap["team_pro"];
            }
            return AnalyticsRolesAndLicensesMap["owner"];
        }),
        user_license: users.map(u => {
            if (u.role === WorkspaceUserRole.GUEST) {
                return TEAM_USER_LICENSE_STATUS.FREE;
            }
            return TEAM_USER_LICENSE_STATUS.TEAM_PRO;
        }),
        ...(analyticsProps ?? {})
    };
    trackActivity("Organization", "InviteSent", null, null, inviteProps, { audit: true });
}

/**
 * Returns legacyanalytics props for workspace
 *
 * Reads slide count on demand for analytics props
 *
 * @param workspaceId The id of the workspace to get props for
 * @returns The analytics props for the workspace
 */
export async function getAnalyticsPropsForWorkspace(workspaceId: string) {
    const uid = auth().currentUser?.uid;
    if (!uid) {
        return {};
    }

    const user: IUserStorageModel = app.user;
    if (user) {
        await user.loadWorkspacesMetadataPromise;
        const workspaceMetadata = user.workspacesMetadata.find(metadata => metadata.id === workspaceId);
        if (workspaceMetadata) {
            return _getAnalyticsProps(workspaceMetadata);
        }
    }

    const metadata = await workspacesMetadataApi.getWorkspacesMetadata({ ids: [workspaceId] });
    if (metadata.length === 0) {
        return {};
    }

    const workspaceMetadata = metadata[0];
    return _getAnalyticsProps(workspaceMetadata);
}

/**
 * Returns the number of slides in all presentations in a workspace.
 *
 * Stateless.
 *
 * @param workspaceId The id of the workspace to get the slide count for
 * @returns The number of slides in all presentations in the workspace
 */
export async function getSlidesInPresentationsCount(workspaceId: string) {
    const { slideCount } = await presentationsApi.getSlideCount({ workspaceId, filter: "all" });
    return slideCount;
}

/**
 * Returns the legacy role for a user in a workspace
 *
 * Stateless if the controller is not initialized
 *
 * @param workspaceId The id of the workspace to get the legacy role for
 * @returns The legacy role for the user in the workspace
 */
export async function getLegacyRoleInWorkspace(workspaceId: string) {
    const uid = auth().currentUser?.uid;
    if (!uid) {
        return null;
    }

    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return getLegacyRoleFromWorkspaceUserRole(workspaceController.workspaceUsers.find(user => user.uid === uid)?.role);
    }

    try {
        const [user] = await workspaceUsersApi.getWorkspaceUsers({ workspaceId, uids: [uid] });
        return getLegacyRoleFromWorkspaceUserRole(user.role);
    } catch (err) {
        logger.error(err, "[WorkspaceController] getLegacyRoleInWorkspace() failed");
        return null;
    }
}

/**
 * Returns the initial workspace ID to show when the app loads
 *
 * Stateless if the controller is not initialized
 *
 * @param workspacesMetadata The list of workspaces metadata
 * @param recentWorkspaceId The most recently selected workspace id from the user record
 * @returns The initial workspace ID to show
 */
export function getInitialWorkspaceId(workspacesMetadata: IWorkspaceMetadata[], recentWorkspaceId?: string | null, roles?: WorkspaceUserRole[]) {
    if (roles) {
        workspacesMetadata = workspacesMetadata.filter(workspace => roles.includes(workspace.userRole));
    }

    if (recentWorkspaceId && workspacesMetadata.some(workspace => workspace.id === recentWorkspaceId)) {
        return recentWorkspaceId;
    }

    if (workspacesMetadata.length === 1) {
        return workspacesMetadata[0].id;
    }

    const nonGuestWorkspaces = workspacesMetadata.filter(workspace => workspace.userRole !== WorkspaceUserRole.GUEST);

    const teamOrEnterpriseWorkspace = nonGuestWorkspaces.find(workspace => workspace.plan.tier === WorkspacePlanTier.TEAM || workspace.plan.tier === WorkspacePlanTier.ENTERPRISE);
    if (teamOrEnterpriseWorkspace) {
        return teamOrEnterpriseWorkspace.id;
    }
    const proWorkspace = nonGuestWorkspaces.find(workspace => workspace.plan.tier === WorkspacePlanTier.PRO);
    if (proWorkspace) {
        return proWorkspace.id;
    }

    if (nonGuestWorkspaces.length > 0) {
        return [...nonGuestWorkspaces.sort((a, b) => a.createdAt - b.createdAt)][0].id;
    }

    return [...workspacesMetadata.sort((a, b) => a.createdAt - b.createdAt)][0]?.id;
}

async function persistWorkspaceIdToUserRecord(workspaceId: string) {
    const existingUserModel = app.user;
    const userUid = existingUserModel?.id ?? auth().currentUser?.uid;
    if (!userUid) {
        return;
    }

    try {
        existingUserModel?.update({ recentWorkspaceId: workspaceId }, { save: false, silent: true });
        await db("users").child(userUid).child("recentWorkspaceId").set(workspaceId);
    } catch (err) {
        logger.error(err, "[WorkspaceController] persistWorkspaceIdToUserRecord() failed");
    }
}

/**
 * Sets the persistent workspace id
 *
 * Does not throw!
 *
 * @param workspaceId The id of the workspace to set
 * @param workspaceAction The action to set (optional)
 */
export function setPersistentWorkspaceId(workspaceId: string, workspaceAction?: "CREATED" | "JOINED") {
    try {
        if (workspaceAction) {
            localStorage.setItem(BEAUTIFUL_WORKSPACE_ACTION, workspaceAction);
        }

        void persistWorkspaceIdToUserRecord(workspaceId);
    } catch (err) {
        logger.error(err, "[WorkspaceController] setPersistentWorkspaceId() failed");
    }
}

/**
 * Returns plan permissions for workspace, falling back to restricted permissions if doesn't have access
 *
 * Stateless if the controller is not initialized
 *
 * @param workspaceId The id of the workspace to get permissions for
 * @returns The workspace plan permissions, or restricted permissions if doesn't have access
 */
export async function getPlanPermissionsForWorkspace(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.planPermissions;
    }

    try {
        const { permissions } = await workspacesBillingApi.getWorkspacePlan({ workspaceId });
        return permissions;
    } catch (err) {
        // If user has no access to the workspace, return restricted permissions instead of throwing
        if (err.status === 403 || err.status === 401) {
            return GET_DEFAULT_RESTRICTED_PLAN_PERMISSIONS();
        }

        throw err;
    }
}

/**
 * Returns action permissions for workspace, falling back to restricted permissions if doesn't have access
 *
 * Stateless if the controller is not initialized
 *
 * @param workspaceId The id of the workspace to get permissions for
 * @returns The workspace action permissions, or restricted permissions if doesn't have access
 */
export async function getActionPermissionsForWorkspace(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.actionPermissions;
    }

    try {
        return await workspaceUsersApi.getUserActionPermissions({ workspaceId });
    } catch (err) {
        if (err.status === 403 || err.status === 401) {
            return GET_DEFAULT_RESTRICTED_ACTION_PERMISSIONS();
        }

        throw err;
    }
}

/**
 * Creates a new workspace
 *
 * Stateless, but will switch workspace and reload related data if the controller is initialized
 */
export async function createWorkspace({ name, analyticsProps }: { name: string, analyticsProps?: { [key: string]: any } }) {
    const workspace = await workspacesApi.createWorkspace({ name });

    if (AppController.initialized) {
        await AppController.user.loadWorkspacesMetadata();
        await AppController.switchWorkspace(workspace.id);
    }
    if (app.user) {
        // UGLY
        await app.user.loadWorkspacesMetadata();
    }

    const createdProps = {
        workspace_id: workspace.id,
        organization_name: name, // Legacy prop, still used in Amplitude
        workspace_name: name,
        ...analyticsProps
    };
    trackActivity("Organization", "Created", null, null, createdProps, { audit: true });
    await Api.klaviyoTrack.post({
        eventName: "Organization:Created",
        createdProps,
    });

    return workspace;
}

/**
 * Deletes a workspace.
 * Allows deleting workspaces only with one user and without active subscription.
 *
 * ☠️☠️☠️ DANGER ☠️☠️☠️
 *
 * This action is not reversible, use with extreme caution
 *
 * ☠️☠️☠️ DANGER ☠️☠️☠️
 *
 * Stateless, but will throw if the controller is initialized with the same workspace
 */
export async function deleteWorkspace(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        throw new Error("Cannot delete current workspace");
    }

    await workspacesApi.deleteWorkspace({ workspaceId });
    trackActivity("Organization", "Deleted", null, null, { workspace_id: workspaceId }, { audit: true });
}

/**
 * Updates a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function updateWorkspace(workspaceId: string, update: UpdateWorkspaceRequest["update"]) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return await workspaceController.updateWorkspace(update);
    }

    return await workspacesApi.updateWorkspace({ workspaceId, update });
}

/**
 * Returns the plan data for a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspacePlan(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.plan;
    }

    return await workspacesBillingApi.getWorkspacePlan({ workspaceId });
}

/**
 * Invites users to a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function inviteWorkspaceUsers(
    workspaceId: string,
    users: Pick<IWorkspaceUserInvite, "email" | "role">[],
    analyticsProps?: { [key: string]: any }
) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        await workspaceController.inviteWorkspaceUsers(users, analyticsProps);
        return workspaceController.userInvites;
    }

    const invites = await workspaceUserInvitesApi.inviteWorkspaceUsers({ workspaceId, users });

    _trackInviteWorkspaceUsers(workspaceId, users, analyticsProps);

    return invites;
}

/**
 * Returns the users for a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspaceUsers(workspaceId: string, uids?: string[], roles?: WorkspaceUserRole[]) {
    const users: IWorkspaceUserExtended[] = [];

    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        if (!uids && !roles) {
            return workspaceController.workspaceUsers;
        }

        users.push(...workspaceController.workspaceUsers.filter(user => uids?.includes(user.uid) || roles?.includes(user.role)));

        const missingUids = uids?.filter(uid => !users.some(user => user.uid === uid)) ?? [];
        const missingUsers = await workspaceUsersApi.getWorkspaceUsers({ workspaceId, uids: missingUids, roles });
        users.push(...missingUsers);

        return users;
    }

    return await workspaceUsersApi.getWorkspaceUsers({ workspaceId, uids, roles });
}

/**
 * Returns the user invites for a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspaceUserInvites(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.userInvites;
    }

    return await workspaceUserInvitesApi.getWorkspaceUserInvites({ workspaceId });
}

/**
 * Returns the workspace data
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspace(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.workspace;
    }

    return await workspacesApi.getWorkspace({ workspaceId });
}

/**
 * Returns the user groups for a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspaceUserGroups(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.userGroups;
    }

    return await workspaceUserGroupsApi.getWorkspaceUserGroups({ workspaceId });
}

/**
 * Returns the workspaces metadata for the current user
 *
 * Stateless
 */
export async function getWorkspacesMetadata({ ids }: { ids?: string[] } = {}) {
    return await workspacesMetadataApi.getWorkspacesMetadata({ ids });
}

/**
 * Returns **composed** user permissions to a resource, use for checking if user has access to a resource and what permissions they have
 *
 * Stateless
 */
export async function getUserPermissionsToResourceFolder({ workspaceId, folderId, uid, includeImplicitPermissions }: { workspaceId: string, folderId: string, uid?: string, includeImplicitPermissions?: boolean }) {
    return await workspaceResourceFoldersApi.getUserPermissionsToResourceFolder({
        workspaceId,
        folderId,
        uid,
        includeImplicitPermissions
    });
}

/**
 * Returns **composed** user permissions to a resource, use for checking if user has access to a resource and what permissions they have
 *
 * Stateless
 */
export async function getUserPermissionsToResource({ workspaceId, resourceId, resourceType, uid }: Parameters<typeof workspaceResourcesApi.getUserPermissionsToResource>[0]) {
    return await workspaceResourcesApi.getUserPermissionsToResource({
        workspaceId,
        resourceId,
        resourceType,
        uid
    });
}

/**
 * Returns a list of workspace permissions to a resource
 *
 * Stateless
 */
export async function getWorkspacePermissionsToResource({ workspaceId, resourceId, resourceType }: Parameters<typeof workspaceResourcesApi.getWorkspacePermissionsToResource>[0]) {
    return await workspaceResourcesApi.getWorkspacePermissionsToResource({
        workspaceId,
        resourceId,
        resourceType
    });
}

/**
 * Returns a list of collaboration invites to a resource
 *
 * Stateless
 */
export async function getCollaborationInvitesToResource({ workspaceId, resourceId, resourceType }: Parameters<typeof workspaceResourcesApi.getCollaborationInvitesToResource>[0]) {
    return await workspaceResourcesApi.getCollaborationInvitesToResource({
        workspaceId,
        resourceId,
        resourceType
    });
}

/**
 * Deletes a user from a workspace
 *
 * Stateless - does not update WorkspaceController state.
 * Use this when removing yourself from a workspace to avoid state refresh errors.
 * For removing other users, use WorkspaceController.deleteWorkspaceUser() instead.
 *
 * Stateless, but will throw if there are no members left in the workspace as it's not possible to leave the workspace if there are no members left.
 */
export async function deleteWorkspaceUser({ workspaceId, uid, transferOption, targetUid }:
    { workspaceId: string, uid: string, transferOption: "other" | "revoke" | "self", targetUid?: string }) {
    const allUsers = await getWorkspaceUsers(workspaceId);

    // This is done to avoid orphaned workspaces.
    if (allUsers.length === 1) {
        throw new ConflictError("Workspace should be deleted instead as it has no remaining members.");
    }

    // If the user is deleting themselves, we need to check if they are deleting the default workspace
    // and if so, throw an error
    if (auth().currentUser.uid === uid) {
        let defaultWorkspaceMetadata: IWorkspaceMetadata = await workspacesMetadataApi.getDefaultWorkspaceMetadata({});
        if (defaultWorkspaceMetadata.id === workspaceId) {
            throw new ConflictError("You cannot leave the default workspace.");
        }
    }

    const { deletedUser, transferredResourceCounts } = await workspaceUsersApi.deleteWorkspaceUser({ workspaceId, uid, transferOption, targetUid });

    //// Legacy analytics ////
    const legacyRole = getLegacyRoleFromWorkspaceUserRole(deletedUser.role);
    const props = {
        workspace_id: workspaceId,
        affected_userid: uid,
        affected_user_role: AnalyticsRolesAndLicensesMap[legacyRole],
        affected_user_license: AnalyticsRolesAndLicensesMap[deletedUser.role === WorkspaceUserRole.GUEST ? "free" : "team_pro"],
        affected_user_presentations_owned: transferredResourceCounts[WorkspaceResourceType.PRESENTATION],
        presentations_transferred_to_personal: transferOption === "self" ? transferredResourceCounts[WorkspaceResourceType.PRESENTATION] : 0,
        presentations_transferred_to_team_member: transferOption === "other" ? transferredResourceCounts[WorkspaceResourceType.PRESENTATION] : 0,
        presentation_recipient_user_id: targetUid
    };
    trackActivity("Organization", "UserRemoved", null, null, props, { audit: true });
    ////

    return deletedUser;
}

/**
 * Returns the number of presentations using a shared theme
 *
 * Stateless
 */
export async function getPresentationsUsingSharedThemeCount({ workspaceId, themeId }: { workspaceId: string, themeId: string }) {
    return await workspaceResourcesApi.getPresentationsUsingSharedThemeCount({ workspaceId, themeId });
}

/**
 * Returns a list of workspace resources
 *
 * Stateless
 */
export async function getWorkspaceResources(request: Parameters<typeof workspaceResourcesApi.getWorkspaceResources>[0]) {
    return await workspaceResourcesApi.getWorkspaceResources(request);
}

/**
 * Returns a list of workspace resources with documents
 *
 * Stateless
 */
export async function getWorkspaceResourcesWithDocuments(request: Parameters<typeof workspaceResourcesApi.getWorkspaceResourcesWithDocuments>[0]) {
    return await workspaceResourcesApi.getWorkspaceResourcesWithDocuments(request);
}

/**
 * Returns a workspace resource
 *
 * Stateless
 */
export async function getWorkspaceResource({ workspaceId, resourceId, resourceType }: { workspaceId: string, resourceId: string, resourceType?: WorkspaceResourceType }) {
    const [resource] = await workspaceResourcesApi.getWorkspaceResources({
        workspaceId,
        resourceTypes: resourceType ? [resourceType] : Object.values(WorkspaceResourceType),
        resourceFilters: [{ field: "resourceId", operator: "eq", value: resourceId }]
    });

    return resource ?? null;
}

/**
 * Returns a list of workspace resource tags
 *
 * Stateless
 */
export async function getWorkspaceResourcesTags(request: Parameters<typeof workspaceResourcesApi.getWorkspaceResourcesTags>[0]) {
    return await workspaceResourcesApi.getWorkspaceResourcesTags(request);
}

/**
 * Returns the workspace id for a resource
 *
 * Stateless
 */
export async function getWorkspaceIdForResource(resourceId: string) {
    return await workspaceResourcesApi.getWorkspaceIdForResource({ resourceId });
}
